import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, waLink, imgUrl } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { OctopusMascot } from "@/components/Mascot";
import ProductImage from "@/components/ProductImage";
import ProductCard from "@/components/ProductCard";

// ---------- HERO ----------
export function Hero() {
  const { content } = useStore();
  const hero = content?.hero || {};
  const imgs = hero.images || [];
  const slots = [0, 1, 2];

  return (
    <section id="inicio" className="relative overflow-hidden bg-soft-gradient" data-testid="hero-section">
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-pink-200/40 blur-3xl" />
      <div className="absolute top-32 -right-20 w-96 h-96 rounded-full bg-purple-200/40 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-cyan-100/50 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            {hero.badge && (
              <span className="inline-flex items-center gap-2 bg-white shadow-soft text-pink-600 text-xs sm:text-sm font-bold px-4 py-2 rounded-full mb-6" data-testid="hero-badge">
                <OctopusMascot size={20} />
                {hero.badge}
              </span>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.08]" data-testid="hero-title">
              {hero.title || "Pequenas ideias, grandes criações em 3D!"}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
              {hero.subtitle || "Produtos criativos, divertidos e úteis feitos em impressão 3D."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/produtos"
                data-testid="hero-cta-products"
                className="bg-brand-gradient text-white font-bold px-7 py-3.5 rounded-full shadow-soft inline-flex items-center gap-2 transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 active:scale-95"
              >
                {hero.cta_primary || "Ver produtos"}
                <ArrowRight size={18} />
              </Link>
              <a
                href={waLink(content, "Olá, Art3D! Vim pelo site e quero conhecer os produtos.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="bg-white border-2 border-[#25D366]/40 text-[#1da851] font-bold px-7 py-3.5 rounded-full inline-flex items-center gap-2 transition-all duration-200 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] hover:-translate-y-0.5 active:scale-95"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.4 14.1c-.2.7-1.3 1.3-1.9 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-4.9-4.3-5.1-4.5-.1-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .6l-.4.6c-.1.2-.3.4-.1.7.1.3.7 1.1 1.5 1.8 1 .9 1.9 1.2 2.1 1.3.3.1.5.1.6-.1l.8-1c.2-.2.4-.2.6-.1l2 1c.3.1.5.2.5.4.1.1.1.6-.1 1.2Z"/></svg>
                {hero.cta_secondary || "Falar no WhatsApp"}
              </a>
            </div>
          </div>

          {/* Composição visual — slots prontos para fotos reais */}
          <div className="relative h-[340px] sm:h-[420px] hidden md:block" data-testid="hero-visual">
            <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-64 rounded-[3rem] bg-brand-gradient opacity-10" />
            {slots.map((i) => (
              <div
                key={i}
                className={`absolute w-40 sm:w-48 aspect-square rounded-3xl bg-white shadow-card overflow-hidden ${
                  i === 0 ? "left-2 top-2 animate-float-slow rotate-[-6deg]" : i === 1 ? "right-2 top-10 animate-float-slower rotate-[5deg]" : "left-1/3 bottom-0 animate-float-slow rotate-[2deg]"
                }`}
                style={{ animationDelay: `${i * 0.8}s` }}
              >
                {imgs[i] ? (
                  <img src={imgUrl(imgs[i])} alt={`Criação Art3D ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <ProductImage src="" label="Sua foto aqui" />
                )}
              </div>
            ))}
            <div className="absolute -bottom-2 right-10">
              <OctopusMascot size={76} wave className="animate-float-slower" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- CATEGORIAS ----------
const CAT_BG = ["bg-pink-50", "bg-purple-50", "bg-cyan-50", "bg-rose-50", "bg-violet-50", "bg-sky-50", "bg-fuchsia-50", "bg-indigo-50"];

export function CategoriesSection() {
  const { content, categories } = useStore();
  const sec = content?.categories_section || {};
  if (!categories.length) return null;
  return (
    <section id="categorias" className="py-16 sm:py-24" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{sec.title || "Categorias"}</h2>
          {sec.subtitle && <p className="mt-2 text-slate-500">{sec.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/produtos?category=${cat.id}`}
              data-testid={`category-card-${cat.id}`}
              className={`group ${CAT_BG[i % CAT_BG.length]} rounded-2xl p-5 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-hover-pink`}
            >
              <div className="aspect-square rounded-xl bg-white/70 overflow-hidden flex items-center justify-center mb-4">
                {cat.image ? (
                  <img src={imgUrl(cat.image)} alt={cat.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <ProductImage src="" label="Imagem da categoria" />
                )}
              </div>
              <h3 className="font-bold text-slate-800 text-sm sm:text-base leading-snug">{cat.name}</h3>
              {cat.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{cat.description}</p>}
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-pink-600 group-hover:gap-2 transition-all">
                Ver produtos <ArrowRight size={13} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- TRILHO DE PRODUTOS (destaques / queridinhos / novidades) ----------
export function ProductRail({ title, subtitle, filter, id, emptyHide = true }) {
  const [products, setProducts] = useState([]);
  const { addToCart } = useStore();

  useEffect(() => {
    api.get("/products", { params: filter }).then(({ data }) => setProducts(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  if (!products.length && emptyHide) return null;

  return (
    <section id={id} className="py-14 sm:py-20" data-testid={`rail-${id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
          </div>
          <Link to="/produtos" className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-pink-600 hover:gap-2.5 transition-all" data-testid={`rail-${id}-see-all`}>
            Ver todos <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.slice(0, 8).map((p) => (
            <ProductCard key={p.id} product={p} onAdd={(prod) => addToCart(prod, 1)} />
          ))}
        </div>
      </div>
    </section>
  );
}
